import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { BASE as API_BASE, setPostAuthRedirect } from '../services/api';
import AdCard from '../components/ads/AdCard';
import { toast } from '../utils/toastBus';
import { Button, Card, EmptyState, Skeleton, SkeletonGroup } from '../components/ui';

const VOICE_GROUP_ORDER = ['male', 'female', 'neutral', 'unknown'];
const VOICE_GROUP_LABELS = {
  male: 'Male',
  female: 'Female',
  neutral: 'Neutral',
  unknown: 'Uncategorized',
};

const VOICE_MALE_HINTS = ['male', 'man', 'men', 'guy', 'gent', 'gentleman', 'mr', 'sir', 'baritone', 'bass', 'deep voice', 'masculine', 'he/him', 'his voice'];
const VOICE_FEMALE_HINTS = ['female', 'woman', 'women', 'lady', 'ms', 'mrs', 'miss', 'feminine', 'soprano', 'alto', 'mezzo', 'she/her', 'her voice'];
const VOICE_NEUTRAL_HINTS = ['neutral', 'androgynous', 'genderless', 'non-binary', 'they/them', 'they them', 'inclusive'];
const KNOWN_VOICE_GENDERS = {
  EXAVITQu4vr4xnSDxMaL: 'male',
  '29vD33N1CtxCmqQRPOHJ': 'male',
  'AZnzlk1XvdvUeBnXmlld': 'male',
  '7Tf3dwM0NC5x3jQmsW9j': 'male',
  '21m00Tcm4TlvDq8ikWAM': 'female',
  'pNInz6obpgDQGcFmaJgB': 'female',
  'ZQe5CZNOzWyzPSCn5a3c': 'female',
  fallback_hype: 'male',
  fallback_chill: 'neutral',
  fallback_neutral: 'neutral',
};
const KNOWN_MALE_NAMES = ['adam', 'adrian', 'antoni', 'anton', 'arthur', 'brandon', 'brian', 'caleb', 'chris', 'christian', 'david', 'dan', 'daniel', 'dylan', 'george', 'henry', 'jack', 'jake', 'james', 'jason', 'jeremy', 'john', 'josh', 'leo', 'liam', 'luke', 'matt', 'matthew', 'michael', 'mike', 'noah', 'ryan', 'sam', 'stephen', 'tom', 'victor', 'will', 'william'];
const KNOWN_FEMALE_NAMES = ['amelia', 'ava', 'bella', 'chloe', 'ella', 'emma', 'grace', 'hannah', 'isabella', 'ivy', 'jasmine', 'kate', 'lily', 'lucy', 'mia', 'natalie', 'nora', 'olivia', 'rachel', 'sophia', 'sophie', 'stella', 'violet', 'zoe'];

function getVoiceId(v) {
  if (!v || typeof v !== 'object') return '';
  return (
    v.id ||
    v.voice_id ||
    v.voiceId ||
    v.providerVoiceId ||
    v.externalId ||
    v.code ||
    v.key ||
    v.meta?.id ||
    v.meta?.voice_id ||
    ''
  );
}

function normalizeGender(g) {
  const normalized = String(g || '').trim().toLowerCase();
  if (!normalized) return null;
  if (['male', 'masculine', 'm'].includes(normalized)) return 'male';
  if (['female', 'feminine', 'f'].includes(normalized)) return 'female';
  if (['neutral', 'androgynous', 'nonbinary', 'non-binary', 'nb', 'n'].includes(normalized)) return 'neutral';
  return 'unknown';
}

function inferGenderFromVoice(raw) {
  const collector = [];
  const pushText = (val) => {
    if (!val) return;
    if (Array.isArray(val)) {
      for (const entry of val) pushText(entry);
      return;
    }
    if (typeof val === 'string') collector.push(val);
  };
  pushText(raw?.name);
  pushText(raw?.label);
  pushText(raw?.displayName);
  pushText(raw?.alias);
  pushText(raw?.description);
  pushText(raw?.meta?.description);
  pushText(raw?.meta?.label);
  pushText(raw?.profile?.description);
  pushText(raw?.profile?.tags);
  pushText(raw?.tags);
  pushText(raw?.attributes?.tags);
  pushText(raw?.style);
  pushText(raw?.persona);
  const combined = collector.map((segment) => String(segment || '').toLowerCase()).join(' | ');
  const tokens = collector
    .flatMap((segment) => String(segment || '')
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter(Boolean));
  const matchesKeyword = (hints) => hints.some((hint) => combined.includes(hint));
  if (!combined) return null;
  if (tokens.some((token) => KNOWN_MALE_NAMES.includes(token))) return 'male';
  if (tokens.some((token) => KNOWN_FEMALE_NAMES.includes(token))) return 'female';
  if (matchesKeyword(VOICE_MALE_HINTS)) return 'male';
  if (matchesKeyword(VOICE_FEMALE_HINTS)) return 'female';
  if (matchesKeyword(VOICE_NEUTRAL_HINTS)) return 'neutral';
  return null;
}

function pickDescriptor(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function formatVoiceLabel(voice) {
  if (!voice) return 'Voice';
  const genderLabel = voice.gender && voice.gender !== 'unknown'
    ? `${voice.gender.charAt(0).toUpperCase()}${voice.gender.slice(1)}`
    : null;
  const descriptors = [];
  if (genderLabel) descriptors.push(genderLabel);
  if (voice.description) descriptors.push(voice.description);
  if (!voice.description && voice.style) descriptors.push(voice.style);
  if (voice.accent) descriptors.push(voice.accent);
  if (voice.provider) descriptors.push(voice.provider);
  const meta = descriptors.filter(Boolean).join(', ');
  return meta ? `${voice.name} — ${meta}` : voice.name;
}

function detectGenderHint(text) {
  if (!text || typeof text !== 'string') return null;
  const lower = text.toLowerCase();

  // Strong male indicators - check these first
  if (/(\bmister\b|\bmr\.?\s*t|\bshout(?:ing|s|er)?\b|\bman\b|\bmale\b|\bboy\b|\bmasculine\b|\bdeep\s+voice\b|\bbaritone\b|\btenor\b)/.test(lower)) {
    return 'male';
  }

  // Strong female indicators
  if (/(\bmrs\.?\b|\bms\.?\b|\bmiss\b|\bwomen?\b|\bfemale\b|\bgirl\b|\bfeminine\b|\bwoman\b|\bsoprano\b|\bhigh\s+voice\b|\bsoft\s+voice\b)/.test(lower)) {
    return 'female';
  }

  // Neutral indicators
  if (/\bnon[-\s]?binary\b|\bneutral\b|\bandrogynous\b|\bthey\b|\bthem\b|\bperson\b/.test(lower)) {
    return 'neutral';
  }

  // Context-based detection for phrases that suggest male
  if (lower.includes('eat like a man') || lower.includes('like a man') || lower.includes('shouting') || lower.includes('shout')) {
    return 'male';
  }

  return null;
}

function normalizeVoiceRecord(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = getVoiceId(raw);
  if (!id) return null;
  let gender = normalizeGender(raw.gender || raw.meta?.gender || raw.attributes?.gender || raw.profile?.gender);
  if ((!gender || gender === 'unknown') && KNOWN_VOICE_GENDERS[id]) {
    gender = KNOWN_VOICE_GENDERS[id];
  }
  if (!gender || gender === 'unknown') {
    const inferred = inferGenderFromVoice(raw);
    if (inferred) gender = inferred;
  }
  const tone = pickDescriptor(raw, ['tone', 'timbre', 'register']);
  const style = pickDescriptor(raw, ['style', 'persona', 'vibe']);
  const accent = pickDescriptor(raw, ['accent', 'locale', 'region']);
  const provider = pickDescriptor(raw, ['provider', 'voice_provider', 'source']);
  const description = [tone, style, accent].filter(Boolean).join(', ');
  return {
    id,
    name: String(raw.name || raw.label || raw.displayName || raw.alias || id),
    gender,
    tone,
    style,
    accent,
    provider,
    description,
    raw,
  };
}

function buildVoiceGroups(voicesList) {
  const buckets = {
    male: [],
    female: [],
    neutral: [],
    unknown: [],
  };
  for (const voice of Array.isArray(voicesList) ? voicesList : []) {
    if (!voice || !voice.id) continue;
    const key = voice.gender || 'unknown';
    buckets[key]?.push(voice);
  }
  for (const key of VOICE_GROUP_ORDER) {
    buckets[key].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }
  return buckets;
}

function computeVoiceNotice(hintKey, voiceId, grouped) {
  if (!hintKey) return '';
  const label = VOICE_GROUP_LABELS[hintKey] || hintKey;
  const group = grouped[hintKey] || [];
  if (group.some((voice) => voice.id === voiceId)) return '';
  const sample = group[0];
  if (sample) return `Prompt suggests a ${label} voice. Consider switching to ${sample.name}.`;
  return `Prompt suggests a ${label} voice, but none are available.`;
}

export default function AdStudio() {
  const navigate = useNavigate();
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState('');
  const [voiceNotice, setVoiceNotice] = useState('');
  const [voiceTouched, setVoiceTouched] = useState(false);
  const [scriptText, setScriptText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [synthBusy, setSynthBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState('');
  const [clips, setClips] = useState([]); // [{id, name, url, size?, source, imageUrl?}]
  const mediaRecRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportUrl, setExportUrl] = useState('');
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoPredictionId, setVideoPredictionId] = useState('');
  const [videoPredictionStatus, setVideoPredictionStatus] = useState('');
  const [videoMeta, setVideoMeta] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoError, setVideoError] = useState('');
  const [adTitle, setAdTitle] = useState('');
  const [adBudget, setAdBudget] = useState('');
  const [adTags, setAdTags] = useState('');
  const [adObjective, setAdObjective] = useState('');
  const [adCategory, setAdCategory] = useState('');
  const [batchSourceUrl, setBatchSourceUrl] = useState('');
  const [batchSegmentsText, setBatchSegmentsText] = useState('');
  const [batchBusy, setBatchBusy] = useState(false);
  // Timeline images and player
  const [timelineImageUrl, setTimelineImageUrl] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [imgGenBusy, setImgGenBusy] = useState(false);
  const [currentClipIndex, setCurrentClipIndex] = useState(-1);
  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const playAllRef = useRef(null); // dedicated audio element for sequential playback
  const videoPollAbort = useRef(null);
  // My Ads list state
  const [myAds, setMyAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsPage, setAdsPage] = useState(1);
  const [adsPageSize] = useState(10);
  const [adsTotal, setAdsTotal] = useState(0);
  const [adsSearch, setAdsSearch] = useState('');
  const [adsSort, setAdsSort] = useState('createdAt:desc');
  const [editing, setEditing] = useState({}); // { [id]: { title, budget } }
  const [showMyAds, setShowMyAds] = useState(false);
  const [voicesLoading, setVoicesLoading] = useState(true);
  // Discover Ads (latest 4)
  const [discoverAds, setDiscoverAds] = useState([]);
  const [discoverLoading, setDiscoverLoading] = useState(true);
  // Ad Composer (prompt -> stream)
  const [composePrompt, setComposePrompt] = useState('');
  const [composeTitle, setComposeTitle] = useState('');
  const [composeDuration, setComposeDuration] = useState(30);
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeResult, setComposeResult] = useState(null); // { item, assets, spec }
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [credits, setCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);

  const handleBuyTokens = useCallback(async (packId = '100') => {
    try {
      const res = await api.startCreditsPurchase(packId);
      if (res?.requiresAuth) {
        setPostAuthRedirect('/profile');
        navigate('/login?redirectTo=/profile');
        return;
      }
      if (res?.error) {
        toast({ type: 'error', message: res.error });
      }
    } catch (err) {
      toast({ type: 'error', message: err?.message || 'Checkout unavailable' });
    }
  }, [navigate]);

  const ensureCreditsAvailable = useCallback(() => {
    if (creditsLoading) return false;
    if (credits === null || Number(credits) <= 0) {
      toast({ type: 'warning', message: 'Purchase credits to use Ad Studio generators.' });
      handleBuyTokens();
      return false;
    }
    return true;
  }, [creditsLoading, credits, handleBuyTokens]);

  const stopVideoPolling = useCallback(() => {
    if (videoPollAbort.current) {
      videoPollAbort.current.abort();
      videoPollAbort.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopVideoPolling();
  }, [stopVideoPolling]);

  async function pollAdVideoStatus(predictionId, signal) {
    let attempts = 0;
    const maxAttempts = 150;
    while (!signal?.aborted && attempts < maxAttempts) {
      const res = await api.adVideoStatus(predictionId);
      if (!res || res.error) return res || { error: 'video_status_failed' };
      if (res.status) setVideoPredictionStatus(res.status);
      if (res.meta) setVideoMeta((prev) => ({ ...(prev || {}), ...res.meta }));
      if (res.status === 'succeeded' || res.status === 'failed' || res.status === 'canceled') {
        return res;
      }
      attempts += 1;
      if (signal?.aborted) break;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    return { error: 'video_status_timeout' };
  }

  const handleGenerateVideo = useCallback(async () => {
    if (videoBusy) return;
    if (!ensureCreditsAvailable()) return;
    if (!exportUrl) {
      toast({ type: 'error', message: 'Export audio before generating video.' });
      return;
    }
    const timelineImageCandidate = timelineImageUrl || (clips.find((c) => c.imageUrl)?.imageUrl || '');
    if (!timelineImageCandidate) {
      toast({ type: 'error', message: 'Add a timeline image before generating video.' });
      return;
    }
    let predictionId = '';
    try {
      stopVideoPolling();
      setVideoBusy(true);
      setVideoError('');
      setVideoPredictionId('');
      setVideoPredictionStatus('');
      setVideoMeta(null);
      setVideoUrl('');
      const durationSec = Math.max(5, Math.round(Number(composeDuration) || 30));
      const startRes = await api.adVideoStart({
        streamId: composeResult?.item?._id || composeResult?.item?.id,
        imageUrl: timelineImageCandidate,
        audioPath: exportUrl,
        durationSec,
        aspectRatio: '16:9',
        prompt: composePrompt || scriptText,
        mode: 'motion'
      });
      if (!startRes || startRes.error) throw new Error(startRes?.error || 'video_start_failed');
      predictionId = String(startRes.predictionId || '');
      if (!predictionId) throw new Error('prediction_missing');
      setVideoPredictionId(predictionId);
      setVideoPredictionStatus(startRes.status || 'starting');
      if (startRes.meta) setVideoMeta(startRes.meta);
      const controller = new AbortController();
      videoPollAbort.current = controller;
      const statusRes = await pollAdVideoStatus(predictionId, controller.signal);
      stopVideoPolling();
      if (!statusRes || statusRes.error || statusRes.status === 'failed' || statusRes.status === 'canceled') {
        const errMsg = statusRes?.error || statusRes?.status || 'video_status_failed';
        setVideoPredictionStatus(statusRes?.status || 'failed');
        throw new Error(errMsg);
      }
      setVideoPredictionStatus(statusRes.status || 'succeeded');
      const finalizeRes = await api.adVideoFinalize({
        predictionId,
        streamId: composeResult?.item?._id || composeResult?.item?.id,
        audioPath: exportUrl,
        imageUrl: timelineImageCandidate,
        durationSec,
        aspectRatio: '16:9'
      });
      if (finalizeRes?.meta) setVideoMeta((prev) => ({ ...(prev || {}), ...finalizeRes.meta }));
      const finalizedUrl = finalizeRes?.item?.videoUrl || finalizeRes?.item?.url || statusRes?.output?.[0] || '';
      if (!finalizedUrl) throw new Error(finalizeRes?.error || 'video_finalize_failed');
      setVideoUrl(resolveMediaUrl(finalizedUrl));
      toast({ type: 'success', message: 'Video ready' });
    } catch (err) {
      if (predictionId) {
        try { await api.adVideoRelease(predictionId); } catch {}
      }
      const rawMessage = err?.message || 'Video generation failed';
      const code = String(rawMessage).trim();
      if (code === 'audio_required') {
        const friendly = 'Audio track missing. Please re-export the timeline audio and try generating the video again.';
        setVideoError(friendly);
        toast({ type: 'error', message: friendly });
      } else {
        setVideoError(rawMessage);
        toast({ type: 'error', message: rawMessage });
      }
      setVideoPredictionId('');
    } finally {
      stopVideoPolling();
      setVideoBusy(false);
    }
  }, [videoBusy, ensureCreditsAvailable, exportUrl, timelineImageUrl, clips, stopVideoPolling, composeDuration, composeResult, composePrompt, scriptText]);

  const voiceHint = useMemo(() => detectGenderHint(`${composePrompt}\n${scriptText}`), [composePrompt, scriptText]);
  const groupedVoices = useMemo(() => buildVoiceGroups(voices), [voices]);
  const selectedVoice = useMemo(() => voices.find((v) => v.id === voiceId) || null, [voices, voiceId]);
  const shouldPromptVoice = useMemo(() => {
    if (!voiceHint || !voiceId) return false;
    const group = groupedVoices[voiceHint] || [];
    return !group.some((voice) => voice.id === voiceId);
  }, [groupedVoices, voiceHint, voiceId]);

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('adstudio_draft');
      if (raw) {
        const d = JSON.parse(raw);
        if (Array.isArray(d?.clips)) setClips(d.clips);
        if (typeof d?.scriptText === 'string') setScriptText(d.scriptText);
        if (typeof d?.voiceId === 'string') setVoiceId(d.voiceId);
        if (typeof d?.timelineImageUrl === 'string') setTimelineImageUrl(d.timelineImageUrl);
      }
    } catch {}
  }, []);

  // Autosave on change
  useEffect(() => {
    try {
      const draft = JSON.stringify({
        clips,
        scriptText,
        voiceId,
        timelineImageUrl,
        t: Date.now(),
      });
      window.localStorage.setItem('adstudio_draft', draft);
    } catch {}
  }, [clips, scriptText, voiceId, timelineImageUrl]);

  useEffect(() => {
    (async () => {
      setVoicesLoading(true);
      try {
        const v = await api.djVoices();
        const list = Array.isArray(v?.items) ? v.items : [];
        const normalized = list.map((voice) => normalizeVoiceRecord(voice)).filter(Boolean);
        setVoices(normalized);
      } catch (e) {
        toast({ type: 'error', message: 'Failed to load voices' });
        setVoices([]);
      } finally {
        setVoicesLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!voices.length) return;
    const grouped = buildVoiceGroups(voices);

    const current = voices.find((v) => v.id === voiceId) || null;

    if (voiceTouched) {
      if (current) return;
      const safe = voices[0]?.id || '';
      if (safe && safe !== voiceId) setVoiceId(safe);
      return;
    }

    if (voiceHint === 'male') {
      if (current?.gender === 'male') return;
      const hintedMale = grouped.male?.[0] || voices.find((voice) => voice.gender === 'male') || null;
      if (hintedMale && hintedMale.id !== voiceId) {
        setVoiceId(hintedMale.id);
        return;
      }
      const neutralCandidate = grouped.neutral?.[0] || voices.find((voice) => voice.gender === 'neutral') || grouped.unknown?.[0] || voices.find((voice) => voice.gender === 'unknown') || null;
      if (neutralCandidate && neutralCandidate.id && neutralCandidate.id !== voiceId) {
        setVoiceId(neutralCandidate.id);
      }
      return;
    } else if (voiceHint === 'female') {
      if (current?.gender === 'female') return;
      const hintedFemale = grouped.female?.[0] || voices.find((voice) => voice.gender === 'female') || null;
      if (hintedFemale && hintedFemale.id !== voiceId) {
        setVoiceId(hintedFemale.id);
        return;
      }
      const neutralCandidate = grouped.neutral?.[0] || voices.find((voice) => voice.gender === 'neutral') || grouped.unknown?.[0] || voices.find((voice) => voice.gender === 'unknown') || null;
      if (neutralCandidate && neutralCandidate.id && neutralCandidate.id !== voiceId) {
        setVoiceId(neutralCandidate.id);
      }
      return;
    } else if (voiceHint === 'neutral') {
      if (current?.gender === 'neutral') return;
      const hintedNeutral = grouped.neutral?.[0] || voices.find((voice) => voice.gender === 'neutral') || null;
      if (hintedNeutral && hintedNeutral.id !== voiceId) {
        setVoiceId(hintedNeutral.id);
        return;
      }
      const unknownCandidate = grouped.unknown?.[0] || voices.find((voice) => voice.gender === 'unknown') || null;
      if (unknownCandidate && unknownCandidate.id && unknownCandidate.id !== voiceId) {
        setVoiceId(unknownCandidate.id);
      }
      return;
    }

    if (current) return;

    const fallback =
      (voiceHint && grouped[voiceHint]?.[0]) ||
      grouped.male?.[0] ||
      grouped.female?.[0] ||
      grouped.neutral?.[0] ||
      grouped.unknown?.[0] ||
      voices[0] ||
      null;

    if (fallback && fallback.id !== voiceId) {
      setVoiceId(fallback.id);
    }
  }, [voices, voiceHint, voiceTouched, voiceId]);

  useEffect(() => {
    setVoiceNotice(computeVoiceNotice(voiceHint, voiceId, groupedVoices));
  }, [voiceHint, voiceId, groupedVoices]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getMyCredits();
        if (r && typeof r.balance === 'number') setCredits(Number(r.balance));
        else setCredits(null);
      } catch {
        setCredits(null);
      } finally {
        setCreditsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setDiscoverLoading(true);
        const res = await api.listAds({ limit: 4, sort: 'createdAt:desc' });
        setDiscoverAds(Array.isArray(res?.items) ? res.items : []);
      } catch {
        setDiscoverAds([]);
      } finally {
        setDiscoverLoading(false);
      }
    })();
  }, []);

  // Load my ads when showMyAds is true
  useEffect(() => {
    if (showMyAds) loadMyAds();
  }, [showMyAds, adsPage, adsSearch, adsSort]);

  async function loadMyAds() {
    setAdsLoading(true);
    try {
      const data = await api.listAds({ page: adsPage, limit: adsPageSize, search: adsSearch, sort: adsSort });
      if (data?.error) throw new Error(data.error);
      setMyAds(Array.isArray(data?.items) ? data.items : []);
      setAdsTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e) {
      toast({ type: 'error', message: e.message || 'Failed to load ads' });
    } finally {
      setAdsLoading(false);
    }
  }

  async function deleteAd(id) {
    if (!window.confirm('Delete this ad?')) return;
    try {
      const res = await api.deleteAd(id);
      if (res?.error || res?.ok === false) throw new Error(res?.error || 'failed_to_delete');
      setMyAds(myAds.filter(a => a._id !== id));
      toast({ type: 'success', message: 'Ad deleted' });
    } catch (e) {
      toast({ type: 'error', message: e.message || 'Delete failed' });
    }
  }

  function startEdit(a) {
    setEditing({ ...editing, [a._id]: { title: a.title || '', budget: a.budget ?? '' } });
  }

  function cancelEdit(id) {
    const copy = { ...editing };
    delete copy[id];
    setEditing(copy);
  }

  function setEditField(id, field, value) {
    setEditing({ ...editing, [id]: { ...editing[id], [field]: value } });
  }

  async function saveEdit(a) {
    const e = editing[a._id];
    if (!e) return;
    try {
      const payload = { title: e.title };
      if (e.budget !== '') payload.budget = Number(e.budget);
      const res = await api.updateAd(a._id, payload);
      if (res?.error) throw new Error(res.error);
      await loadMyAds();
      cancelEdit(a._id);
      toast({ type: 'success', message: 'Ad updated' });
    } catch (err) {
      toast({ type: 'error', message: err.message || 'Update failed' });
    }
  }

  const onDrop = async (e) => {
    e.preventDefault();
    try {
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      await handleFileUpload(file);
    } catch {}
  };

  const handleFileInput = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFileUpload(file);
    // reset input so same file can be re-selected
    e.target.value = '';
  };

async function handleFileUpload(file) {
  if (!ensureCreditsAvailable()) return;
  setUploading(true);
  setUploadPct(0);
  setError('');
  try {
    const res = await api.uploadFile(file, (pct) => setUploadPct(pct));
    if (res?.item?.url) {
      addClip({ name: res.item.name || file.name, url: resolveMediaUrl(res.item.url), source: 'upload' });
      toast({ type: 'success', message: 'Upload added to timeline' });
    } else {
      throw new Error(res?.error || 'Upload failed');
    }
  } catch (e) {
    setError(e?.message || 'Upload failed');
  } finally {
    setUploading(false);
    setTimeout(() => setUploadPct(0), 600);
  }
}

function addClip({ name, url, source }) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  setClips((prev) => [...prev, { id, name, url, source }]);
}

function removeClip(id) {
  setClips((prev) => prev.filter((c) => c.id !== id));
}

function moveClip(idx, dir) {
  setClips((prev) => {
    const i = idx;
    const j = dir < 0 ? Math.max(0, i - 1) : Math.min(prev.length - 1, i + 1);
    if (i === j) return prev;
    const copy = [...prev];
    const [item] = copy.splice(i, 1);
    copy.splice(j, 0, item);
    return copy;
  });
}

async function onSynthesize(e) {
  e.preventDefault();
  if (!ensureCreditsAvailable()) return;
  if (shouldPromptVoice && !window.confirm(`Your prompt hints at a ${VOICE_GROUP_LABELS[voiceHint] || voiceHint} voice. Continue with the current selection?`)) {
    return;
  }
  setSynthBusy(true);
  setError('');
  try {
    if (!scriptText.trim()) throw new Error('Script text required');
    const { blob, error: err } = await api.djSpeak({ text: scriptText, voiceId });
    if (err) throw new Error(err);
    // Upload TTS blob so backend can access it for concat
    const ttsFile = new File([blob], `tts-${Date.now()}.wav`, { type: blob.type || 'audio/wav' });
    const res = await api.uploadFile(ttsFile);
    if (res?.item?.url) {
      addClip({ name: res.item.name || ttsFile.name, url: resolveMediaUrl(res.item.url), source: 'tts' });
      toast({ type: 'success', message: 'Synthesized voice added to timeline' });
    } else {
      throw new Error(res?.error || 'Failed to upload TTS result');
    }
  } catch (e2) {
    setError(e2?.message || 'Synthesis failed');
  } finally {
    setSynthBusy(false);
  }
}

async function onRecordToggle() {
  try {
    if (!recording) {
      if (!ensureCreditsAvailable()) return;
      // start
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: pickSupportedAudioMime() });
      mediaStreamRef.current = stream;
      mediaRecRef.current = rec;
      const chunks = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
      rec.onstop = async () => {
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
          // Upload the recording so it persists and becomes playable via backend origin
          const file = new File([blob], `recording-${Date.now()}.webm`, { type: blob.type });
          await handleFileUpload(file);
        } catch (e) {
          setError('Recording upload failed');
        } finally {
          cleanupStream();
        }
      };
      rec.start();
      setRecording(true);
    } else {
      // stop
      const rec = mediaRecRef.current;
      if (rec && rec.state !== 'inactive') rec.stop();
      setRecording(false);
    }
  } catch (e) {
    setError('Microphone access denied or unsupported');
    cleanupStream();
  }
}

function cleanupStream() {
  try { mediaRecRef.current = null; } catch {}
  try { mediaStreamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
  mediaStreamRef.current = null;
}

function resolveMediaUrl(u) {
  if (!u || typeof u !== 'string') return '';
  if (u.startsWith('/uploads/')) return `${API_BASE}${u}`;
  return u;
}

return (
  <div style={{ padding: 24 }}>
    {/* Title centered */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
      <h2 style={{ color: '#ff7a45', margin: 0, textAlign: 'center' }}>Ad Studio</h2>
    </div>
    {!creditsLoading && (credits === null || Number(credits) <= 0) && (
      <div style={{ border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)', color: '#fbbf24', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Tokens required</div>
        <div style={{ fontSize: 14, color: '#fde68a' }}>
          Purchase credits to use Ad Studio generators. <button onClick={() => handleBuyTokens()} style={{ background: 'transparent', border: 0, color: '#fbbf24', textDecoration: 'underline', cursor: 'pointer' }}>Buy tokens</button>
        </div>
      </div>
    )}
    {/* Ad Composer (Prompt -> Stream) */}
    <Card title="Ad Composer" subtitle="Turn a single marketing prompt into an ad stream (audio + visual)">
      <div style={{ display: 'grid', gap: 12 }}>
        <textarea
          placeholder="Paste your marketing prompt here..."
          value={composePrompt}
          onChange={(e) => setComposePrompt(e.target.value)}
          rows={5}
          style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12 }}>
          <input
            placeholder="Optional title (defaults to 'Ad Stream')"
            value={composeTitle}
            onChange={(e) => setComposeTitle(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}
          />
          <input
            type="number"
            min={5}
            max={120}
            step={5}
            value={composeDuration}
            onChange={(e) => setComposeDuration(Number(e.target.value) || 30)}
            style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button
            loading={composeBusy}
            disabled={composeBusy || !composePrompt.trim() || (!creditsLoading && (credits === null || Number(credits) <= 0))}
            onClick={async () => {
              try {
                if (!ensureCreditsAvailable()) return;
                setComposeBusy(true);
                setComposeResult(null);
                const res = await api.adComposeFull({ prompt: composePrompt, durationSec: composeDuration, title: composeTitle });
                if (res?.error) throw new Error(res.error);
                setComposeResult(res);
                // Prefill tags from Use Case when available
                try {
                  const uc = res?.spec?.useCase;
                  if (typeof uc === 'string' && uc.trim() && !adTags.trim()) setAdTags(uc.trim());
                } catch {}
                toast({ type: 'success', message: 'Ad composed successfully' });
              } catch (e) {
                toast({ type: 'error', message: e?.message || 'Compose failed' });
              } finally {
                setComposeBusy(false);
              }
            }}
          >{composeBusy ? 'Composing…' : 'Compose Ad'}</Button>
          <span style={{ color: '#6b7280', fontSize: 12 }}>Uses backend `/api/adstudio/compose/full` with provider fallbacks</span>
        </div>
        {composeResult?.item && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Link to={`/streams/${encodeURIComponent(String(composeResult.item._id || composeResult.item.id))}`} style={{ color: '#93c5fd' }}>Open Stream</Link>
              {composeResult.assets?.audioPath && (
                <a href={composeResult.assets.audioPath.startsWith('/uploads/') ? `${API_BASE}${composeResult.assets.audioPath}` : composeResult.assets.audioPath} target="_blank" rel="noreferrer" style={{ color: '#93c5fd' }}>Audio</a>
              )}
              {composeResult.assets?.imageUrl && (
                <a href={composeResult.assets.imageUrl.startsWith('/uploads/') ? `${API_BASE}${composeResult.assets.imageUrl}` : composeResult.assets.imageUrl} target="_blank" rel="noreferrer" style={{ color: '#93c5fd' }}>Image</a>
              )}
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {composeResult.assets?.imageUrl && (
                <img
                  src={composeResult.assets.imageUrl.startsWith('/uploads/') ? `${API_BASE}${composeResult.assets.imageUrl}` : composeResult.assets.imageUrl}
                  alt="composed"
                  style={{ maxWidth: 320, borderRadius: 8, border: '1px solid #374151' }}
                />
              )}
              {composeResult.assets?.audioPath && (
                <audio
                  src={composeResult.assets.audioPath.startsWith('/uploads/') ? `${API_BASE}${composeResult.assets.audioPath}` : composeResult.assets.audioPath}
                  controls
                  preload="metadata"
                  style={{ width: '100%' }}
                />
              )}
              {composeResult?.spec?.useCase && (
                <div style={{ color: '#9ca3af', fontSize: 13 }}>
                  <strong style={{ color: '#e5e7eb' }}>Use Case:</strong> {composeResult.spec.useCase}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
    {/* Advanced toggle */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
      <button
        type="button"
        onClick={() => setShowAdvanced(v => !v)}
        style={{ background: '#1f2937', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}
      >{showAdvanced ? '▾ Hide advanced timeline' : '▸ Show advanced timeline'}</button>
      <span style={{ color: '#6b7280', fontSize: 12 }}>Optional: manual uploads, TTS synthesis, and timeline export</span>
    </div>
    {showAdvanced && (
      <p style={{ color: '#9ca3af', marginTop: 0 }}>Upload or record audio, synthesize voice from script, and arrange a simple timeline.</p>
    )}

    {/* Upload / Drag Drop */}
    {showAdvanced && (
      <section style={{ margin: '16px 0', padding: 16, border: '1px solid #374151', borderRadius: 12, background: '#111827' }}
        onDragOver={(e) => { e.preventDefault(); }} onDrop={onDrop}>
        <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Upload</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input type="file" accept="audio/*,video/*" onChange={handleFileInput} />
          <span style={{ color: '#9ca3af', fontSize: 12 }}>or drag and drop a file here</span>
          <button onClick={onRecordToggle} style={btnSecondary}>{recording ? 'Stop Recording' : 'Record Microphone'}</button>
        </div>
        {uploading && (
          <div style={{ marginTop: 8 }}>
            <div style={{ height: 6, width: '100%', background: '#0b0b0e', borderRadius: 999 }}>
              <div style={{ height: '100%', width: `${uploadPct}%`, background: '#ff5a1f', transition: 'width 120ms ease' }} />
            </div>
            <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>Uploading… {uploadPct}%</div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <Button
            loading={exportBusy}
            disabled={exportBusy || !clips.length || (!creditsLoading && (credits === null || Number(credits) <= 0))}
            onClick={async () => {
              try {
                if (!ensureCreditsAvailable()) return;
                setExportBusy(true);
                const sources = clips.map(c => c.url);
                if (!sources.length) throw new Error('No clips to export');
                const out = await api.concatAudio({ sources });
                if (out?.item?.url) {
                  setExportUrl(out.item.url);
                  toast({ type: 'success', message: 'Export complete' });
                } else {
                  throw new Error(out?.error || 'Export failed');
                }
              } catch (ex) {
                setError(ex?.message || 'Export failed');
              } finally {
                setExportBusy(false);
              }
            }}
            style={btnPrimary}
          >
            {exportBusy ? 'Exporting…' : 'Export Timeline'}
          </Button>
          {exportUrl && (
            <Card title="✅ Timeline Exported Successfully!" style={{ marginTop: 16 }}>
              <div
                style={{
                  padding: '12px 16px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                <p style={{ margin: 0, color: '#10b981', fontWeight: 500 }}>🎉 Your ad audio is ready!</p>
                <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#9ca3af' }}>Preview it below, then save it as an ad or download the file</p>
              </div>

              <audio src={resolveMediaUrl(exportUrl)} controls preload="metadata" style={{ width: '100%', marginBottom: 16 }} />

              <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
                <input
                  placeholder="Ad title *"
                  value={adTitle}
                  onChange={(e) => setAdTitle(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#0b0b0e',
                    color: '#e5e7eb',
                    border: '1px solid #374151',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 16,
                  }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                  <input
                    placeholder="Budget (optional)"
                    type="number"
                    value={adBudget}
                    onChange={(e) => setAdBudget(e.target.value)}
                    style={{
                      background: '#0b0b0e',
                      color: '#e5e7eb',
                      border: '1px solid #374151',
                      borderRadius: 8,
                      padding: '10px 12px',
                    }}
                  />
                  <input
                    placeholder="Objective (optional)"
                    value={adObjective}
                    onChange={(e) => setAdObjective(e.target.value)}
                    style={{
                      background: '#0b0b0e',
                      color: '#e5e7eb',
                      border: '1px solid #374151',
                      borderRadius: 8,
                      padding: '10px 12px',
                    }}
                  />
                  <input
                    placeholder="Category (optional)"
                    value={adCategory}
                    onChange={(e) => setAdCategory(e.target.value)}
                    style={{
                      background: '#0b0b0e',
                      color: '#e5e7eb',
                      border: '1px solid #374151',
                      borderRadius: 8,
                      padding: '10px 12px',
                    }}
                  />
                </div>
                <input
                  placeholder="Tags (comma-separated, optional)"
                  value={adTags}
                  onChange={(e) => setAdTags(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#0b0b0e',
                    color: '#e5e7eb',
                    border: '1px solid #374151',
                    borderRadius: 8,
                    padding: '10px 12px',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Button
                  onClick={async () => {
                    try {
                      if (!adTitle.trim()) {
                        toast({ type: 'error', message: 'Ad title is required' });
                        return;
                      }
                      if (!ensureCreditsAvailable()) {
                        toast({ type: 'warning', message: 'Purchase credits to use Ad Studio generators.' });
                        handleBuyTokens();
                        return false;
                      }
                      const clipsMeta = clips.map((c) => ({ name: c.name, url: c.url, imageUrl: c.imageUrl || null }));
                      const payload = {
                        title: adTitle.trim(),
                        scriptText,
                        voiceId,
                        targeting: {
                          mediaUrl: exportUrl,
                          timelineImageUrl: timelineImageUrl || undefined,
                          clipsMeta,
                          tags: adTags ? adTags.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
                          objective: adObjective || undefined,
                          category: adCategory || undefined,
                        },
                      };
                      if (adBudget !== '') payload.budget = Number(adBudget);
                      const res = await api.createAd(payload);
                      if (res?.item?._id) {
                        toast({ type: 'success', message: 'Ad saved successfully! View it in "My Ads" below.' });
                        loadAds();
                        setAdTitle('');
                        setAdBudget('');
                        setAdTags('');
                        setAdObjective('');
                        setAdCategory('');
                      } else {
                        throw new Error(res?.error || 'Failed to save ad');
                      }
                    } catch (e) {
                      toast({ type: 'error', message: e?.message || 'Failed to save ad' });
                    }
                  }}
                  size="lg"
                >
                  💾 Save As Ad
                </Button>
                <a
                  href={resolveMediaUrl(exportUrl)}
                  download={`${adTitle || 'ad'}.mp3`}
                  style={{
                    padding: '10px 20px',
                    background: '#374151',
                    color: '#e5e7eb',
                    borderRadius: 8,
                    textDecoration: 'none',
                    fontSize: 16,
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  ⬇️ Download Audio
                </a>
              </div>
            </Card>
          )}
          <button
            onClick={() => {
              setClips([]);
              setScriptText('');
              setExportUrl('');
              try { window.localStorage.removeItem('adstudio_draft'); } catch {}
            }}
            style={btnSecondary}
          >
            Clear Draft
          </button>
        </div>
      </section>
    )}

    {/* Video Generation */}
    {exportUrl && (
      <Card title="Generate Video" subtitle="Create a motion video from your exported timeline">
        <div style={{ display: 'grid', gap: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button
              loading={videoBusy}
              disabled={videoBusy || !exportUrl || !timelineImageUrl || (!creditsLoading && (credits === null || Number(credits) <= 0))}
              onClick={handleGenerateVideo}
              size="lg"
            >
              {videoBusy ? `Generating Video... (${videoPredictionStatus || 'Starting'})` : '🎬 Generate Video'}
            </Button>
            <span style={{ color: '#6b7280', fontSize: 12 }}>
              Uses backend `/api/adstudio/video/start` with Replicate fallbacks
            </span>
          </div>
          {videoPredictionStatus && (
            <div style={{
              padding: '8px 12px',
              background: videoPredictionStatus === 'succeeded' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
              border: `1px solid ${videoPredictionStatus === 'succeeded' ? '#10b981' : '#f59e0b'}`,
              borderRadius: 6,
              color: videoPredictionStatus === 'succeeded' ? '#10b981' : '#f59e0b',
              fontSize: 13
            }}>
              Status: {videoPredictionStatus}
              {videoMeta?.provider && ` • Provider: ${videoMeta.provider}`}
              {videoMeta?.mode && ` • Mode: ${videoMeta.mode}`}
            </div>
          )}
          {videoError && (
            <div style={{
              padding: '8px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              borderRadius: 6,
              color: '#ef4444',
              fontSize: 13
            }}>
              Error: {videoError}
            </div>
          )}
          {videoUrl && (
            <div style={{ display: 'grid', gap: 12 }}>
              <video
                src={videoUrl}
                controls
                preload="metadata"
                style={{ width: '100%', maxHeight: 400, borderRadius: 8, border: '1px solid #374151' }}
              />
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a
                  href={videoUrl}
                  download={`${adTitle || 'ad-video'}.mp4`}
                  style={{
                    padding: '8px 16px',
                    background: '#374151',
                    color: '#e5e7eb',
                    borderRadius: 8,
                    textDecoration: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  ⬇️ Download Video
                </a>
                <Button
                  onClick={() => {
                    if (!adTitle.trim()) {
                      toast({ type: 'error', message: 'Enter a title before saving' });
                      return;
                    }
                    // TODO: Implement save video to ads
                    toast({ type: 'info', message: 'Save video functionality coming soon' });
                  }}
                  size="sm"
                >
                  💾 Save as Ad
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    )}

    {/* My Ads Section */}
    <section style={{ margin: '16px 0', padding: 16, border: '1px solid #374151', borderRadius: 12, background: '#111827' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#e5e7eb' }}>My Ads</h3>
        <button onClick={() => setShowMyAds(!showMyAds)} style={btnSecondary}>
          {showMyAds ? 'Hide' : 'Show'} ({adsTotal})
        </button>
      </div>

      {adsLoading ? (
        <SkeletonGroup count={3} variant="card" height="120px" />
      ) : myAds.length === 0 ? (
        <EmptyState
          icon="📢"
          title="No ads yet"
          description="Create and save your first ad using the tools above to see it here"
        />
      ) : (
        <div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
            {myAds.map((ad) => {
              const isEditing = !!editing[ad._id];
              return (
                <li key={ad._id} style={{ border: '1px solid #1f2937', borderRadius: 8, padding: 12, background: '#0b0b0e' }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input
                        value={editing[ad._id].title}
                        onChange={(e) => setEditField(ad._id, 'title', e.target.value)}
                        placeholder="Title"
                        style={{ padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#111827', color: '#e5e7eb' }}
                      />
                      <input
                        type="number"
                        value={editing[ad._id].budget}
                        onChange={(e) => setEditField(ad._id, 'budget', e.target.value)}
                        placeholder="Budget"
                        style={{ padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#111827', color: '#e5e7eb' }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => saveEdit(ad)} style={btnPrimary}>Save</button>
                        <button onClick={() => cancelEdit(ad._id)} style={btnSecondary}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: 0, color: '#e5e7eb' }}>{ad.title}</h4>
                        <p style={{ margin: '4px 0', color: '#9ca3af', fontSize: 14 }}>
                          Budget: ${ad.budget || 0} • Created: {new Date(ad.createdAt).toLocaleDateString()}
                        </p>
                        {ad.audioUrl && (
                          <audio src={ad.audioUrl} controls preload="metadata" style={{ marginTop: 8, maxWidth: 300 }} />
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => startEdit(ad)} style={btnSecondary}>Edit</button>
                        <button onClick={() => deleteAd(ad._id)} style={btnDanger}>Delete</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Pagination */}
          {adsTotal > adsPageSize && (
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
              <button
                onClick={() => setAdsPage((p) => Math.max(1, p - 1))}
                disabled={adsPage === 1}
                style={{ ...btnSecondary, opacity: adsPage === 1 ? 0.5 : 1 }}
              >Previous</button>
              <span style={{ color: '#9ca3af' }}>
                Page {adsPage} of {Math.ceil(adsTotal / adsPageSize)}
              </span>
              <button
                onClick={() => setAdsPage((p) => Math.min(Math.ceil(adsTotal / adsPageSize), p + 1))}
                disabled={adsPage >= Math.ceil(adsTotal / adsPageSize)}
                style={{ ...btnSecondary, opacity: adsPage >= Math.ceil(adsTotal / adsPageSize) ? 0.5 : 1 }}
              >Next</button>
            </div>
          )}
        </div>
      )}
    </section>

    {/* Discover Ads (latest 4) */}
    <section style={{ margin: '16px 0', padding: 16, border: '1px solid #374151', borderRadius: 12, background: '#111827' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#e5e7eb' }}>Discover Ads</h3>
        <Link to="/discover" style={{ color: '#93c5fd' }}>Open Discover →</Link>
      </div>
      {discoverLoading ? (
        <div style={{ color: '#9ca3af' }}>Loading…</div>
      ) : discoverAds.length === 0 ? (
        <div style={{ color: '#9ca3af' }}>No ads yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          {discoverAds.map((a) => (
            <AdCard
              key={a._id || a.id}
              ad={a}
              isOwner={false}
              onDeleted={() => {
                (async () => {
                  try {
                    const res = await api.listAds({ limit: 4, sort: 'createdAt:desc' });
                    setDiscoverAds(Array.isArray(res?.items) ? res.items : []);
                  } catch {}
                })();
              }}
              onUpdated={() => { /* no-op for discover */ }}
            />
          ))}
        </div>
      )}
    </section>

    {/* TTS Synthesis */}
    {showAdvanced && (
      <Card title="Voice Synthesis" subtitle="Generate audio from text using AI voices">
        {voicesLoading ? (
          <div style={{ marginBottom: 16 }}>
            <Skeleton variant="button" width="200px" style={{ marginBottom: 8 }} />
            <Skeleton variant="text" width="100%" height="100px" />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <label htmlFor="ad-voice" style={{ color: '#9ca3af', minWidth: 60 }}>Voice</label>
              <select
                id="ad-voice"
                value={voiceId}
                onChange={(e) => {
                  setVoiceTouched(true);
                  setVoiceId(e.target.value);
                }}
                style={selectStyle}
              >
                {VOICE_GROUP_ORDER.map((groupKey) => {
                  const group = groupedVoices[groupKey];
                  if (!group || group.length === 0) return null;
                  return (
                    <optgroup key={groupKey} label={VOICE_GROUP_LABELS[groupKey] || groupKey}>
                      {group.map((voice) => (
                        <option key={voice.id} value={voice.id}>{formatVoiceLabel(voice)}</option>
                      ))}
                    </optgroup>
                  );
                })}
                {voices.length === 0 && <option value="">No voices</option>}
              </select>
            </div>
            {voiceNotice && (
              <div
                style={{
                  border: '1px solid #f59e0b',
                  background: 'rgba(245,158,11,0.1)',
                  color: '#fcd34d',
                  borderRadius: 8,
                  padding: '8px 10px',
                  marginBottom: 12,
                  fontSize: 12,
                }}
              >
                {voiceNotice}
              </div>
            )}
            <textarea
              id="ad-script"
              rows={4}
              placeholder="Write your script here…"
              value={scriptText}
              onChange={(e) => setScriptText(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 8,
                border: '1px solid #374151',
                background: '#0b0b0e',
                color: '#e5e7eb',
                fontSize: 16,
                fontFamily: 'inherit',
                marginBottom: 12,
              }}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Button
                disabled={synthBusy || !scriptText.trim() || (!creditsLoading && (credits === null || Number(credits) <= 0))}
                onClick={onSynthesize}
                loading={synthBusy}
              >
                {synthBusy ? 'Synthesizing...' : 'Synthesize & Add'}
              </Button>
              <span style={{ color: '#9ca3af', fontSize: 14 }}>Generates an audio clip and adds it to the timeline</span>
            </div>
          </>
        )}
      </Card>
    )}

    {/* Timeline Controls (Play All + Images) */}
    {showAdvanced && (
      <Card title="Timeline" subtitle={`${clips.length} clip${clips.length !== 1 ? 's' : ''} in timeline`}>
        {/* Timeline image picker */}
        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          <label style={{ color: '#9ca3af' }}>Timeline Image (shown before playback starts)</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="https://example.com/image.jpg"
              value={timelineImageUrl}
              onChange={(e) => setTimelineImageUrl(e.target.value)}
              style={{ flex: 1, padding: 8, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}
            />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const res = await api.uploadFile(file);
                  const url = res?.item?.url ? (res.item.url.startsWith('/uploads/') ? `${API_BASE}${res.item.url}` : res.item.url) : '';
                  if (!url) throw new Error('Upload failed');
                  setTimelineImageUrl(url);
                  toast({ type: 'success', message: 'Timeline image uploaded' });
                } catch (e2) {
                  toast({ type: 'error', message: e2?.message || 'Image upload failed' });
                } finally {
                  e.target.value = '';
                }
              }}
            />
          </div>
          {(timelineImageUrl || (isPlayingAll && currentClipIndex >= 0 && clips[currentClipIndex]?.imageUrl)) && (
            <div style={{ marginTop: 8 }}>
              <img
                src={(isPlayingAll && currentClipIndex >= 0 && clips[currentClipIndex]?.imageUrl) ? clips[currentClipIndex].imageUrl : timelineImageUrl}
                alt="timeline-visual"
                style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid #374151' }}
              />
            </div>
          )}
        </div>

        {/* Generate Image (Replicate) */}
        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          <label style={{ color: '#9ca3af' }}>Generate Image (optional)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
            <input
              placeholder="Describe the scene (e.g., modern product showcase on gradient background)"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              style={{ padding: 8, borderRadius: 8, border: '1px solid #374151', background: '#111827', color: '#e5e7eb' }}
            />
            <Button
              disabled={imgGenBusy || !imagePrompt.trim() || (!creditsLoading && (credits === null || Number(credits) <= 0))}
              loading={imgGenBusy}
              onClick={async () => {
                try {
                  if (!ensureCreditsAvailable()) return;
                  setImgGenBusy(true);
                  const res = await api.generateImage({ prompt: imagePrompt, aspect_ratio: '16:9' });
                  if (res?.item?.url) {
                    setTimelineImageUrl(res.item.url);
                    toast({ type: 'success', message: 'Image generated and applied to timeline' });
                  } else {
                    throw new Error(res?.error || 'Failed to generate image');
                  }
                } catch (e) {
                  toast({ type: 'error', message: e?.message || 'Image generation failed' });
                } finally {
                  setImgGenBusy(false);
                }
              }}
            >Generate</Button>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>Requires backend REPLICATE_API_TOKEN. Rate limited with graceful fallbacks.</p>
        </div>

        {/* Play All controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <Button
            disabled={clips.length === 0}
            onClick={async () => {
              if (clips.length === 0) return;
              try { document.querySelectorAll('audio').forEach((audioEl) => { try { audioEl.pause(); } catch {} }); } catch {}
              setCurrentClipIndex((i) => (i >= 0 ? i : 0));
              setIsPlayingAll(true);
              setTimeout(async () => {
                const index = currentClipIndex >= 0 ? currentClipIndex : 0;
                if (!clips[index]) return;
                const el = playAllRef.current;
                if (!el) return;
                el.src = clips[index].url;
                try { await el.play(); } catch {}
              }, 0);
            }}
          >
            ▶ Play All
          </Button>
          <Button
            disabled={!isPlayingAll}
            onClick={() => {
              try { playAllRef.current?.pause(); } catch {}
              setIsPlayingAll(false);
            }}
          >
            ⏸ Pause
          </Button>
          <Button
            disabled={clips.length === 0}
            onClick={() => {
              setIsPlayingAll(false);
              try {
                const el = playAllRef.current;
                if (el) {
                  el.pause();
                  el.currentTime = 0;
                  el.src = '';
                }
              } catch {}
              setCurrentClipIndex(-1);
            }}
          >
            ⏹ Stop
          </Button>
          <Button
            disabled={currentClipIndex <= 0}
            onClick={async () => {
              const prevIdx = Math.max(0, currentClipIndex - 1);
              setCurrentClipIndex(prevIdx);
              try {
                const el = playAllRef.current;
                if (!el) return;
                el.src = clips[prevIdx].url;
                await el.play();
                setIsPlayingAll(true);
              } catch {}
            }}
          >
            ⏮ Prev
          </Button>
          <Button
            disabled={currentClipIndex < 0 || currentClipIndex >= clips.length - 1}
            onClick={async () => {
              const nextIdx = Math.min(clips.length - 1, currentClipIndex + 1);
              setCurrentClipIndex(nextIdx);
              try {
                const el = playAllRef.current;
                if (!el) return;
                el.src = clips[nextIdx].url;
                await el.play();
                setIsPlayingAll(true);
              } catch {}
            }}
          >
            ⏭ Next
          </Button>
          <audio
            ref={playAllRef}
            onEnded={() => {
              setTimeout(() => {
                setCurrentClipIndex((idx) => {
                  const next = idx + 1;
                  if (next >= clips.length) {
                    try {
                      const el = playAllRef.current;
                      if (el) {
                        el.pause();
                        el.currentTime = 0;
                      }
                    } catch {}
                    setIsPlayingAll(false);
                    return -1;
                  }
                  try {
                    const el = playAllRef.current;
                    if (el) {
                      el.src = clips[next].url;
                      el.play?.();
                    }
                  } catch {}
                  return next;
                });
              }, 0);
            }}
            preload="metadata"
            style={{ display: 'none' }}
          />
        </div>

        {clips.length === 0 ? (
          <EmptyState
            icon="🎵"
            title="No clips yet"
            description="Upload audio files, record your voice, or use AI synthesis to add clips to your timeline"
          />
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
            {clips.map((clip, idx) => (
              <li
                key={clip.id}
                style={{ border: '1px solid #1f2937', borderRadius: 8, padding: 8, display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 12 }}
              >
                <span style={{ color: '#9ca3af', width: 24, textAlign: 'right' }}>{idx + 1}.</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <audio src={clip.url} controls preload="metadata" style={{ maxWidth: 300 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 200 }}>
                    <span style={{ color: '#e5e7eb' }}>{clip.name}</span>
                    <span style={{ color: '#9ca3af', fontSize: 12 }}>Source: {clip.source}</span>
                    <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                      <input
                        placeholder="Clip image URL (optional)"
                        value={clip.imageUrl || ''}
                        onChange={(e) => setClips((prev) => prev.map((existing, i) => (i === idx ? { ...existing, imageUrl: e.target.value } : existing)))}
                        style={{ padding: 6, borderRadius: 6, border: '1px solid #374151', background: '#111827', color: '#e5e7eb' }}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const res = await api.uploadFile(file);
                            const url = res?.item?.url ? (res.item.url.startsWith('/uploads/') ? `${API_BASE}${res.item.url}` : res.item.url) : '';
                            if (!url) throw new Error('Upload failed');
                            setClips((prev) => prev.map((existing, i) => (i === idx ? { ...existing, imageUrl: url } : existing)));
                            toast({ type: 'success', message: 'Clip image uploaded' });
                          } catch (e2) {
                            toast({ type: 'error', message: e2?.message || 'Image upload failed' });
                          } finally {
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <button onClick={() => moveClip(idx, -1)} style={btnTiny}>↑</button>
                  <button onClick={() => moveClip(idx, +1)} style={btnTiny}>↓</button>
                  <button onClick={() => removeClip(clip.id)} style={btnDanger}>Remove</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    )}

    {/* My Ads Section */}
    <section style={{ margin: '16px 0', padding: 16, border: '1px solid #374151', borderRadius: 12, background: '#111827' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#e5e7eb' }}>My Ads</h3>
        <button onClick={() => setShowMyAds(!showMyAds)} style={btnSecondary}>
          {showMyAds ? 'Hide' : 'Show'} ({adsTotal})
        </button>
      </div>

      {adsLoading ? (
        <SkeletonGroup count={3} variant="card" height="120px" />
      ) : myAds.length === 0 ? (
        <EmptyState
          icon="📢"
          title="No ads yet"
          description="Create and save your first ad using the tools above to see it here"
        />
      ) : (
        <div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
            {myAds.map((ad) => {
              const isEditing = !!editing[ad._id];
              return (
                <li key={ad._id} style={{ border: '1px solid #1f2937', borderRadius: 8, padding: 12, background: '#0b0b0e' }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input
                        value={editing[ad._id].title}
                        onChange={(e) => setEditField(ad._id, 'title', e.target.value)}
                        placeholder="Title"
                        style={{ padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#111827', color: '#e5e7eb' }}
                      />
                      <input
                        type="number"
                        value={editing[ad._id].budget}
                        onChange={(e) => setEditField(ad._id, 'budget', e.target.value)}
                        placeholder="Budget"
                        style={{ padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#111827', color: '#e5e7eb' }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => saveEdit(ad)} style={btnPrimary}>Save</button>
                        <button onClick={() => cancelEdit(ad._id)} style={btnSecondary}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: 0, color: '#e5e7eb' }}>{ad.title}</h4>
                        <p style={{ margin: '4px 0', color: '#9ca3af', fontSize: 14 }}>
                          Budget: ${ad.budget || 0} • Created: {new Date(ad.createdAt).toLocaleDateString()}
                        </p>
                        {ad.audioUrl && (
                          <audio src={ad.audioUrl} controls preload="metadata" style={{ marginTop: 8, maxWidth: 300 }} />
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => startEdit(ad)} style={btnSecondary}>Edit</button>
                        <button onClick={() => deleteAd(ad._id)} style={btnDanger}>Delete</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Pagination */}
          {adsTotal > adsPageSize && (
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
              <button
                onClick={() => setAdsPage((p) => Math.max(1, p - 1))}
                disabled={adsPage === 1}
                style={{ ...btnSecondary, opacity: adsPage === 1 ? 0.5 : 1 }}
              >Previous</button>
              <span style={{ color: '#9ca3af' }}>
                Page {adsPage} of {Math.ceil(adsTotal / adsPageSize)}
              </span>
              <button
                onClick={() => setAdsPage((p) => Math.min(Math.ceil(adsTotal / adsPageSize), p + 1))}
                disabled={adsPage >= Math.ceil(adsTotal / adsPageSize)}
                style={{ ...btnSecondary, opacity: adsPage >= Math.ceil(adsTotal / adsPageSize) ? 0.5 : 1 }}
              >Next</button>
            </div>
          )}
        </div>
      )}
    </section>

    {error && (
      <div style={{ background: '#2b0d0d', border: '1px solid #7f1d1d', color: '#fecaca', borderRadius: 8, padding: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{String(error)}</span>
        <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: '#ef4444', color: '#0b0b0e', border: 0, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>Dismiss</button>
      </div>
    )}
  </div>
);
}

function pickSupportedAudioMime() {
  const types = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/ogg'];
  for (const t of types) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) return t;
  }
  return 'audio/webm';
}

const btnPrimary = { background: '#ff5a1f', color: '#fff', border: 0, borderRadius: 8, padding: '8px 12px', cursor: 'pointer' };
const btnSecondary = { background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 8, padding: '8px 12px', cursor: 'pointer' };
const btnDanger = { background: '#ef4444', color: '#fff', border: 0, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' };
const btnTiny = { background: '#374151', color: '#e5e7eb', border: 0, borderRadius: 6, padding: '4px 8px', cursor: 'pointer' };
const selectStyle = { background: '#0b0b0e', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: 8 };
